import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Description as DocIcon,
  PictureAsPdf as PdfIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/api';
import SimpleMarkdown from './SimpleMarkdown';
import { exportElementToPdf } from '../utils/exportElementPdf';

const CloseWindowHint = ({ placement = 'absolute' } = {}) => (
  <Box
    sx={{
      ...(placement === 'absolute'
        ? { position: 'absolute', top: 10, right: 56, zIndex: 2 }
        : { position: 'relative', flexShrink: 0 }),
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      maxWidth: { xs: 170, sm: 260 },
      px: 1.1,
      py: 0.55,
      borderRadius: 1,
      border: '2px solid #d32f2f',
      bgcolor: '#ffebee',
      boxShadow: '0 0 0 2px rgba(211,47,47,0.15)',
      animation: 'tenanthubCloseHintBlink 1.1s ease-in-out infinite',
      '@keyframes tenanthubCloseHintBlink': {
        '0%, 100%': { opacity: 1, transform: 'translateX(0)' },
        '50%': { opacity: 0.45, transform: 'translateX(-2px)' }
      }
    }}
  >
    <Typography
      variant="caption"
      sx={{
        color: '#b71c1c',
        fontWeight: 800,
        lineHeight: 1.25,
        fontSize: { xs: '0.65rem', sm: '0.72rem' }
      }}
    >
      Click Close to leave this window
    </Typography>
    <ArrowForwardIcon sx={{ color: '#d32f2f', fontSize: 20, flexShrink: 0 }} />
  </Box>
);

const ReleaseNotesReaderDialog = ({
  open,
  onClose,
  service,
  version,
  label
}) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [note, setNote] = React.useState(null);
  const [pdfExporting, setPdfExporting] = React.useState(false);
  const pdfCaptureRef = React.useRef(null);

  React.useEffect(() => {
    if (!open || !service || !version) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      setNote(null);
      try {
        const res = await api.get(`/settings/release-notes/${encodeURIComponent(service)}/${encodeURIComponent(version)}`);
        if (!cancelled) setNote(res.data?.data || null);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Failed to load release notes');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, service, version]);

  const handleExportPdf = async () => {
    try {
      if (!pdfCaptureRef.current || !note?.content) {
        toast.error('Release notes are not ready to export');
        return;
      }
      setPdfExporting(true);
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const safeService = String(service || label || 'service').replace(/[^\w.-]+/g, '_');
      const safeVersion = String(version || 'current').replace(/[^\w.-]+/g, '_');
      const fileName = `tenanthub-release-notes-${safeService}-${safeVersion}-${stamp}.pdf`;
      await exportElementToPdf(pdfCaptureRef.current, { fileName });
      toast.success(`PDF downloaded: ${fileName}`);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to generate PDF');
    } finally {
      setPdfExporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose?.();
      }}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      scroll="paper"
      BackdropProps={{
        sx: { backgroundColor: 'rgba(15, 23, 42, 0.62)' }
      }}
    >
      <DialogTitle sx={{ pr: { xs: 7, sm: 34 }, position: 'relative' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <DocIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Release notes
          </Typography>
        </Stack>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Chip size="small" label={label || service || 'Service'} color="primary" variant="outlined" />
            <Chip
              size="small"
              label={version || '—'}
              sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontWeight: 700 }}
            />
            {note?.filename && (
              <Chip size="small" label={note.filename} variant="outlined" />
            )}
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              data-html2canvas-ignore="true"
              variant="contained"
              color="error"
              size="small"
              startIcon={pdfExporting ? <CircularProgress size={14} color="inherit" /> : <PdfIcon />}
              disabled={pdfExporting || loading || !note?.content}
              onClick={handleExportPdf}
              sx={{ fontWeight: 700 }}
            >
              {pdfExporting ? 'Creating PDF…' : 'Download PDF'}
            </Button>
          </Box>
        </Stack>
        <CloseWindowHint />
        <IconButton
          data-html2canvas-ignore="true"
          aria-label="Close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            border: '2px solid #d32f2f',
            bgcolor: '#ffebee',
            '&:hover': { bgcolor: '#ffcdd2' }
          }}
        >
          <CloseIcon sx={{ color: '#c62828' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && error && <Alert severity="warning">{error}</Alert>}
        {!loading && !error && note?.content && (
          <Box
            ref={pdfCaptureRef}
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: 2,
              bgcolor: '#ffffff',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Stack spacing={1.25} sx={{ mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <DocIcon color="primary" fontSize="small" />
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  TenantHub release notes
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={label || service || 'Service'} color="primary" variant="outlined" />
                <Chip
                  size="small"
                  label={version || '—'}
                  sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontWeight: 700 }}
                />
                {note.filename && <Chip size="small" label={note.filename} variant="outlined" />}
              </Stack>
              {note.updated_at && (
                <Typography variant="caption" color="text.secondary">
                  File updated: {new Date(note.updated_at).toLocaleString()}
                </Typography>
              )}
            </Stack>
            <Box
              sx={{
                p: { xs: 1, sm: 2 },
                borderRadius: 2,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <SimpleMarkdown content={note.content} />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.25
        }}
        data-html2canvas-ignore="true"
      >
        <Button
          variant="contained"
          color="error"
          startIcon={pdfExporting ? <CircularProgress size={14} color="inherit" /> : <PdfIcon />}
          disabled={pdfExporting || loading || !note?.content}
          onClick={handleExportPdf}
          sx={{ fontWeight: 700 }}
        >
          {pdfExporting ? 'Creating PDF…' : 'Download PDF'}
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, ml: 'auto' }}>
          <CloseWindowHint placement="inline" />
          <Button
            onClick={onClose}
            variant="contained"
            color="error"
            sx={{ fontWeight: 700, border: '2px solid #b71c1c' }}
          >
            Close
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ReleaseNotesReaderDialog;
